// eslint-disable-next-line import/no-cycle
import levelSystemService from '../../services/levelSystemService';

export default {
  namespaced: true,

  state: {
    registryEntries: [],
    levelTargets: [],
    userStats: {
      totalUsers: 0,
      activeUsers: 0,
      avgLevel: 0,
      totalXP: 0,
      levelDistribution: [],
      recentProgress: [],
    },
    auditLogs: [],
    auditLogsTotal: 0,
    auditLogsPage: 1,
    auditLogsLimit: 20,
    auditLogsTotalPages: 0,
    userHistory: null,
    groupAverage: null,
    loading: false,
    error: null,
  },

  mutations: {
    SET_REGISTRY_ENTRIES(state, entries) {
      state.registryEntries = entries;
    },

    SET_LEVEL_TARGETS(state, levels) {
      state.levelTargets = levels;
    },

    SET_USER_STATS(state, stats) {
      state.userStats = stats;
    },

    ADD_REGISTRY_ENTRY(state, entry) {
      state.registryEntries.push(entry);
    },

    UPDATE_REGISTRY_ENTRY(state, updatedEntry) {
      const index = state.registryEntries.findIndex((e) => e._id === updatedEntry._id);
      if (index !== -1) {
        state.registryEntries.splice(index, 1, updatedEntry);
      }
    },

    REMOVE_REGISTRY_ENTRY(state, entryId) {
      state.registryEntries = state.registryEntries.filter((e) => e._id !== entryId);
    },

    ADD_LEVEL_TARGET(state, level) {
      state.levelTargets.push(level);
    },

    UPDATE_LEVEL_TARGET(state, updatedLevel) {
      const index = state.levelTargets.findIndex((l) => l._id === updatedLevel._id);
      if (index !== -1) {
        state.levelTargets.splice(index, 1, updatedLevel);
      }
    },

    REMOVE_LEVEL_TARGET(state, levelId) {
      state.levelTargets = state.levelTargets.filter((l) => l._id !== levelId);
    },

    SET_LOADING(state, isLoading) {
      state.loading = isLoading;
    },

    SET_ERROR(state, error) {
      state.error = error;
    },

    SET_AUDIT_LOGS_RESULT(
      state,
      {
        data, total, page, limit, totalPages,
      },
    ) {
      state.auditLogs = data || [];
      state.auditLogsTotal = total ?? 0;
      state.auditLogsPage = page ?? 1;
      state.auditLogsLimit = limit ?? 20;
      state.auditLogsTotalPages = totalPages ?? 0;
    },

    SET_USER_HISTORY(state, payload) {
      state.userHistory = payload;
    },

    SET_GROUP_AVERAGE(state, payload) {
      state.groupAverage = payload;
    },
  },

  actions: {
    async initializeRegistry({ commit, dispatch }) {
      try {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);

        const response = await levelSystemService.initializeRegistry();
        await dispatch('fetchRegistryEntries');

        return response.data || response;
      } catch (error) {
        commit('SET_ERROR', error.message);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async fetchRegistryEntries({ commit }) {
      try {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);

        const response = await levelSystemService.getRegistryEntries();
        const entries = response.data || response;
        commit('SET_REGISTRY_ENTRIES', entries);

        return entries;
      } catch (error) {
        commit('SET_ERROR', error.message);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async createRegistryEntry({ commit, dispatch }, entryData) {
      try {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);

        const response = await levelSystemService.createRegistryEntry(entryData);
        const newEntry = response.data || response;
        commit('ADD_REGISTRY_ENTRY', newEntry);

        // Show success notification
        dispatch(
          'notifications/showToast',
          {
            severity: 'success',
            summary: 'Success',
            detail: 'Registry entry created successfully!',
            life: 3000,
          },
          { root: true },
        );

        return newEntry;
      } catch (error) {
        commit('SET_ERROR', error.message);

        // Show error notification
        dispatch(
          'notifications/showToast',
          {
            severity: 'error',
            summary: 'Error',
            detail: error.message || 'Failed to create registry entry',
            life: 5000,
          },
          { root: true },
        );

        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async updateRegistryEntry({ commit, dispatch }, { id, entryData }) {
      try {
        console.log('🔄 Vuex updateRegistryEntry called with:', { id, entryData });

        commit('SET_LOADING', true);
        commit('SET_ERROR', null);

        console.log('📡 Calling levelSystemService.updateRegistryEntry...');
        const response = await levelSystemService.updateRegistryEntry(id, entryData);
        console.log('📥 Service response:', response);

        const updatedEntry = response.data || response;
        console.log('💾 Committing UPDATE_REGISTRY_ENTRY with:', updatedEntry);
        commit('UPDATE_REGISTRY_ENTRY', updatedEntry);

        // Show success notification
        dispatch(
          'notifications/showToast',
          {
            severity: 'success',
            summary: 'Success',
            detail: 'Registry entry updated successfully!',
            life: 3000,
          },
          { root: true },
        );

        return updatedEntry;
      } catch (error) {
        console.error('❌ Vuex updateRegistryEntry error:', error);
        commit('SET_ERROR', error.message);

        // Show error notification
        dispatch(
          'notifications/showToast',
          {
            severity: 'error',
            summary: 'Error',
            detail: error.message || 'Failed to update registry entry',
            life: 5000,
          },
          { root: true },
        );

        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async deleteRegistryEntry({ commit, dispatch }, entryId) {
      try {
        console.log('🗑️ Vuex deleteRegistryEntry called with entryId:', entryId);

        commit('SET_LOADING', true);
        commit('SET_ERROR', null);

        console.log('📡 Calling levelSystemService.deleteRegistryEntry...');
        await levelSystemService.deleteRegistryEntry(entryId);
        console.log('✅ Service delete successful, committing REMOVE_REGISTRY_ENTRY');

        commit('REMOVE_REGISTRY_ENTRY', entryId);

        // Show success notification
        dispatch(
          'notifications/showToast',
          {
            severity: 'success',
            summary: 'Success',
            detail: 'Registry entry deleted successfully!',
            life: 3000,
          },
          { root: true },
        );
      } catch (error) {
        console.error('❌ Vuex deleteRegistryEntry error:', error);
        commit('SET_ERROR', error.message);

        // Show error notification
        dispatch(
          'notifications/showToast',
          {
            severity: 'error',
            summary: 'Error',
            detail: error.message || 'Failed to delete registry entry',
            life: 5000,
          },
          { root: true },
        );

        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async fetchLevelTargets({ commit }) {
      try {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);

        const response = await levelSystemService.getLevelTargets();
        const levels = response.data || response;
        commit('SET_LEVEL_TARGETS', levels);

        return levels;
      } catch (error) {
        commit('SET_ERROR', error.message);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async createLevelTarget({ commit }, levelData) {
      try {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);

        const response = await levelSystemService.createLevelTarget(levelData);
        const newLevel = response.data || response;
        commit('ADD_LEVEL_TARGET', newLevel);

        return newLevel;
      } catch (error) {
        commit('SET_ERROR', error.message);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async updateLevelTarget({ commit }, { id, levelData }) {
      try {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);

        const response = await levelSystemService.updateLevelTarget(id, levelData);
        const updatedLevel = response.data || response;
        commit('UPDATE_LEVEL_TARGET', updatedLevel);

        return updatedLevel;
      } catch (error) {
        commit('SET_ERROR', error.message);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async getLevelAssignmentStatus({ commit }, levelId) {
      try {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);

        const response = await levelSystemService.getLevelAssignmentStatus(levelId);
        return response.data || response;
      } catch (error) {
        commit('SET_ERROR', error.message);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async deleteLevelTarget({ commit }, { levelId, deleteOption }) {
      try {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);

        const response = await levelSystemService.deleteLevelTarget(levelId, deleteOption);
        const result = response.data || response;

        if (result.success) {
          // Remove from local state
          commit('REMOVE_LEVEL_TARGET', levelId);
        }

        return result;
      } catch (error) {
        commit('SET_ERROR', error.message);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async fetchUserStats({ commit }) {
      try {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);

        const response = await levelSystemService.getUserStats();
        const stats = response.data || response;
        commit('SET_USER_STATS', stats);

        return stats;
      } catch (error) {
        commit('SET_ERROR', error.message);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async fetchUserHistory({ commit }, userEmail) {
      try {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);
        commit('SET_USER_HISTORY', null);

        const response = await levelSystemService.getHistoryByEmail(userEmail);
        const data = response.data || response;
        commit('SET_USER_HISTORY', data);

        return data;
      } catch (error) {
        commit('SET_ERROR', error.message);
        commit('SET_USER_HISTORY', null);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async fetchGroupAverage({ commit }) {
      try {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);

        const response = await levelSystemService.getGroupAverage();
        const data = response.data || response;
        commit('SET_GROUP_AVERAGE', data);

        return data;
      } catch (error) {
        commit('SET_ERROR', error.message);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async recalculateUserLevelPreview({ commit }, userId) {
      try {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);

        const response = await levelSystemService.recalculateUserLevelPreview(userId);
        const data = response?.data ?? response;

        return data;
      } catch (error) {
        commit('SET_ERROR', error.message);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async recalculateUserLevel({ commit }, userId) {
      try {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);

        const response = await levelSystemService.recalculateUserLevel(userId);
        const data = response?.data ?? response;

        return data;
      } catch (error) {
        commit('SET_ERROR', error.message);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async exportUserHistory({ commit, dispatch }, params = {}) {
      try {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);

        const response = await levelSystemService.exportUserHistory(params);
        const blob = response.data || response;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `user-level-history-${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        dispatch(
          'notifications/showToast',
          {
            severity: 'success',
            summary: 'Success',
            detail: 'User history exported successfully',
            life: 3000,
          },
          { root: true },
        );
      } catch (error) {
        commit('SET_ERROR', error.message);
        dispatch(
          'notifications/showToast',
          {
            severity: 'error',
            summary: 'Error',
            detail: error.message || 'Export failed',
            life: 5000,
          },
          { root: true },
        );
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async assignLevelToUser({ commit }, { userId, levelNumber }) {
      try {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);

        const response = await levelSystemService.assignLevelToUser(userId, levelNumber);
        return response.data || response;
      } catch (error) {
        commit('SET_ERROR', error.message);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async updateUserGoalProgress(
      { commit },
      {
        userId,
        level,
        goalId,
        progress,
      },
    ) {
      try {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);

        const response = await levelSystemService.updateUserGoalProgress(
          userId,
          level,
          goalId,
          progress,
        );
        return response.data || response;
      } catch (error) {
        commit('SET_ERROR', error.message);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async getUserProgressSummary({ commit }, userId) {
      try {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);

        const response = await levelSystemService.getUserProgressSummary(userId);
        return response.data || response;
      } catch (error) {
        commit('SET_ERROR', error.message);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async assignAllLevelsToUser({ commit }, { userId }) {
      try {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);

        const response = await levelSystemService.assignAllLevelsToUser({ userId });
        return response.data || response;
      } catch (error) {
        commit('SET_ERROR', error.message);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async fetchAuditLogs({ commit }, { filters = {}, page = 1, limit = 20 } = {}) {
      try {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);

        const params = { page, limit, ...filters };
        if (params.startDate instanceof Date) params.startDate = params.startDate.toISOString();
        if (params.endDate instanceof Date) params.endDate = params.endDate.toISOString();

        const response = await levelSystemService.getAuditLogs(params);
        const result = response.data || response;
        commit('SET_AUDIT_LOGS_RESULT', {
          data: result.data,
          total: result.total,
          page: result.page,
          limit: result.limit,
          totalPages: result.totalPages,
        });

        return result;
      } catch (error) {
        commit('SET_ERROR', error.message);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async exportAuditLogs({ commit, dispatch }, filters = {}) {
      try {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);

        const params = { ...filters };
        if (params.startDate instanceof Date) params.startDate = params.startDate.toISOString();
        if (params.endDate instanceof Date) params.endDate = params.endDate.toISOString();

        if (
          params.startDate == null
          || params.startDate === ''
          || params.endDate == null
          || params.endDate === ''
        ) {
          throw new Error('Start date and end date are required to export the audit log.');
        }
        const start = new Date(params.startDate);
        const end = new Date(params.endDate);
        if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
          throw new Error('Invalid start or end date for export.');
        }
        if (start.getTime() > end.getTime()) {
          throw new Error('Start date must be before or equal to end date.');
        }
        const exportMaxSpanMs = 366 * 24 * 60 * 60 * 1000;
        if (end.getTime() - start.getTime() > exportMaxSpanMs) {
          throw new Error('Date range cannot exceed 366 days.');
        }

        const response = await levelSystemService.exportAuditLogs(params);
        const blob = response.data || response;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `level-system-audit-log-${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        dispatch(
          'notifications/showToast',
          {
            severity: 'success',
            summary: 'Success',
            detail: 'Audit log exported successfully',
            life: 3000,
          },
          { root: true },
        );
      } catch (error) {
        commit('SET_ERROR', error.message);
        dispatch(
          'notifications/showToast',
          {
            severity: 'error',
            summary: 'Error',
            detail: error.message || 'Failed to export audit log',
            life: 5000,
          },
          { root: true },
        );
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },
  },

  getters: {
    getRegistryEntryByType: (state) => (type) => state
      .registryEntries.find((entry) => entry.type === type),

    getLevelTargetByNumber: (state) => (levelNumber) =>
      state.levelTargets.find((level) => level.level === levelNumber), // eslint-disable-line

    getSortedLevelTargets: (state) => [...state.levelTargets].sort((a, b) => a.level - b.level),

    getRegistryEntriesByGoalType: (state) => (goalType) => state
      .registryEntries.filter((entry) => entry.goalType === goalType), // eslint-disable-line

    isLoading: (state) => state.loading,

    hasError: (state) => !!state.error,

    getError: (state) => state.error,
  },
};
